// src/context/DatabaseContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import FavoritesUpdateContext from './FavoritesUpdateContext';

// Créer le contexte
const DatabaseContext = createContext();

// Hook personnalisé
export const useDatabase = () => useContext(DatabaseContext);

// Provider du contexte
export const DatabaseProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    query: '',
    oem: [],
    system: [],
    type: []
  });

  // Nous n'avons plus besoin de cette référence
  // car nous utilisons directement les filtres dans useEffect
  
  // Importer le contexte de mise à jour des favoris
  const { updateTrigger } = useContext(FavoritesUpdateContext) || { updateTrigger: 0 };

  // Charger les articles depuis l'API
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchArticles = async () => {
      setLoading(true);
      try {
        // Utiliser l'URL complète avec le port 5001 (fixed-server.js)
        // Construire les paramètres de requête manuellement
        let url = 'http://localhost:5001/api/articles';
        const queryParams = [];
        
        // Ajouter les filtres seulement s'ils sont sélectionnés
        if (filters.oem && filters.oem.length > 0) {
          filters.oem.forEach(oem => {
            queryParams.push(`OEM=${encodeURIComponent(oem)}`);
          });
        }
        
        if (filters.system && filters.system.length > 0) {
          filters.system.forEach(system => {
            queryParams.push(`System=${encodeURIComponent(system)}`);
          });
        }
        
        if (filters.type && filters.type.length > 0) {
          filters.type.forEach(type => {
            queryParams.push(`Type=${encodeURIComponent(type)}`);
          });
        }
        
        if (filters.query) {
          queryParams.push(`query=${encodeURIComponent(filters.query)}`);
        }
        
        // Ajouter l'ID utilisateur si disponible (pour les favoris)
        const { user } = JSON.parse(localStorage.getItem('authState') || '{}');
        if (user && user._id) {
          queryParams.push(`userId=${encodeURIComponent(user._id)}`);
        }
        
        // Ajouter un paramètre de cache-busting pour forcer le rechargement
        queryParams.push(`_t=${Date.now()}`);
        
        // Ajouter les paramètres à l'URL
        if (queryParams.length > 0) {
          url += '?' + queryParams.join('&');
        }
        
        console.log('URL avec filtres:', url);
        
        const response = await axios.get(url, {
          signal: controller.signal
        });
        
        if (response.data.success) {
          setDocuments(response.data.data);
          setFilteredDocuments(response.data.data);
          setError(null);
        } else {
          setError('Error loading articles');
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Error loading articles:', err);
          setError('Server connection error');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchArticles();
    
    // Nettoyage pour éviter les mises à jour sur un composant démonté
    return () => {
      controller.abort();
    };
  }, [
    // Dépendance stable pour éviter les boucles infinies
    JSON.stringify({
      query: filters.query,
      oem: filters.oem,
      system: filters.system,
      type: filters.type
    }),
    updateTrigger // Ajouter le déclencheur de mise à jour comme dépendance
  ]);

  // Mettre à jour un filtre
  const updateFilter = (filterName, value) => {
    console.log(`Filter update ${filterName}:`, value);
    
    // Convertir les noms de filtres si nécessaire
    let normalizedFilterName = filterName.toLowerCase();
    
    // Mapper les noms de filtres
    const filterMap = {
      'oem': 'oem',
      'system': 'system',
      'type': 'type',
      'query': 'query'
    };
    
    const mappedFilterName = filterMap[normalizedFilterName] || normalizedFilterName;
    
    // Vérifier que le nom du filtre est valide
    if (!['query', 'oem', 'system', 'type'].includes(mappedFilterName)) {
      console.error(`Nom de filtre invalide: ${mappedFilterName} (original: ${filterName})`);
      return;
    }
    
    console.log(`Filtre normalisé: ${mappedFilterName}, valeur:`, value);
    
    setFilters(prev => ({
      ...prev,
      [mappedFilterName]: value
    }));
  };

  // Réinitialiser tous les filtres
  const resetFilters = () => {
    setFilters({
      query: '',
      oem: [],
      system: [],
      type: []
    });
  };

  // Obtenir les options de filtrage depuis l'API
  const [filterOptions, setFilterOptions] = useState({
    oem: ['Renault', 'Stellantis', 'Opel'],
    system: ['BIW', 'Chassis', 'Battery'],
    type: ['Linkedin', 'Patent', 'Article']
  });
  
  // Charger les options de filtrage
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get('http://localhost:5001/api/articles/stats');
        if (response.data.success) {
          // Adapter les noms des options pour correspondre à ceux attendus par le composant FilterPanel
          setFilterOptions({
            oem: response.data.availableValues.OEM || [],
            system: response.data.availableValues.System || [],
            type: response.data.availableValues.Type || []
          });
        }
      } catch (err) {
        console.error('Erreur lors du chargement des options de filtrage:', err);
      }
    };
    
    fetchFilterOptions();
  }, []);

  // Valeur fournies par le contexte
  const value = React.useMemo(() => ({
    documents: filteredDocuments,
    loading,
    error,
    filters,
    updateFilter,
    resetFilters,
    filterOptions
  }), [filteredDocuments, loading, error, filters, filterOptions]);

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

export default DatabaseContext;